import React, { useState, useEffect, useCallback } from 'react';
import { withTranslation } from '../../../ChildrenContext.js';
import PropTypes from 'prop-types';
import DropdownList from 'react-widgets/DropdownList';
import CustomModal from '../../../Modal';
import FormGroup from '../../../FormGroup';
import { I18nFormatter } from '../../../../utils/I18n/I18n';
import CurrencyActions from '../../../../actions/CurrencyActions';

const ProfileAccountSettings = ({
    prevCurrency,
    balanceAmount,
    currency,
    currenciesList,
    rates,
    modalIsVisible,
    loading,
    convertCurrentCurrency,
    t
}) => {
    const [state, setState] = useState({
        prevCurrency: prevCurrency || '',
        balanceAmount: balanceAmount || '',
        currency: currency || '',
        currenciesList: currenciesList || [],
        rates: rates || {},
        modalIsVisible: modalIsVisible || false,
        loading: loading || false
    });

    useEffect(() => {
        CurrencyActions.getCurrencies();
        CurrencyActions.getCurrentCurrencyRatios();
    }, []);

    useEffect(() => {
        setState(prevState => ({
            ...prevState,
            balanceAmount: balanceAmount || '',
            currency: currency || '',
            currenciesList: currenciesList || [],
            rates: rates || {}
        }));
    }, [balanceAmount, currency, currenciesList, rates]);

    const showModal = useCallback(() => {
        setState(prevState => ({
            ...prevState,
            modalIsVisible: true
        }));
    }, []);

    const closeModal = useCallback(() => {
        setState(prevState => ({
            ...prevState,
            modalIsVisible: false,
            currency: prevState.prevCurrency
        }));
    }, []);

    const handleCurrencyChange = useCallback((item) => {
        setState(prevState => ({
            ...prevState,
            prevCurrency: prevState.currency,
            currency: item.title
        }));

        showModal();
    }, [showModal]);

    const getConvertRatioValue = useCallback(() => {
        if (state.rates && state.currency && state.prevCurrency) {
            return parseFloat(state.rates[state.prevCurrency][state.currency].bid).toFixed(6);
        }
        return 0;
    }, [state.rates, state.currency, state.prevCurrency]);

    const getBalanceAfterConverting = useCallback(() => {
        return parseFloat(state.balanceAmount * getConvertRatioValue()).toFixed(2);
    }, [state.balanceAmount, getConvertRatioValue]);

    const handleConvertCurrentCurrency = useCallback(() => {
        setState(prevState => ({
            ...prevState,
            loading: true
        }));

        if (convertCurrentCurrency) {
            convertCurrentCurrency(state, onSuccess);
        }
    }, [state, convertCurrentCurrency]);

    const onSuccess = useCallback(() => {
        setState(prevState => ({
            ...prevState,
            loading: false,
            modalIsVisible: false
        }));
    }, []);

    const convertRatioValue = getConvertRatioValue();
    const balanceAfterConverting = getBalanceAfterConverting();
    const staticNames = t?.profilePage?.accountSettings || {};

    return (
        <React.Fragment>
            <CustomModal
                visible={state.modalIsVisible}
                onClose={closeModal}
                title={staticNames.currency}
                onConfirm={handleConvertCurrentCurrency}
                loading={state.loading}
            >
                <div className="form-horizontal">
                    <FormGroup>
                        <div className="col-sm-8">{staticNames.currentBalance}:</div>
                        {state.prevCurrency
                            ? <div className="col-sm-4 text-right">{I18nFormatter.currency(state.balanceAmount, state.prevCurrency)}</div>
                            : null
                        }
                    </FormGroup>
                    <FormGroup>
                        <div className="col-sm-8">{staticNames.buyCurrency}:</div>
                        <div className="col-sm-4 text-right">{state.currency}</div>
                    </FormGroup>
                    <FormGroup>
                        <div className="col-sm-8">{staticNames.convertRatio} {state.prevCurrency}/{state.currency}</div>
                        <div className="col-sm-4 text-right">{I18nFormatter.currency(convertRatioValue, state.currency)}</div>
                    </FormGroup>
                    <FormGroup>
                        <div className="col-sm-8">{staticNames.nextBalance}:</div>
                        <div className="col-sm-4 text-right"><span className="helvetica">~</span>{I18nFormatter.currency(balanceAfterConverting, state.currency)}</div>
                    </FormGroup>
                </div>
            </CustomModal>

            <FormGroup
                label={`${staticNames.currency}:`}
                className={{"currency-form": true}}
            >
                <DropdownList
                    className={'form-control'}
                    dataKey="title"
                    textField="title"
                    data={state.currenciesList}
                    value={state.currency}
                    onChange={handleCurrencyChange}
                />
            </FormGroup>
        </React.Fragment>
    );
};

ProfileAccountSettings.propTypes = {
    currency: PropTypes.string,
    prevCurrency: PropTypes.string,
    balanceAmount: PropTypes.string,
    modalIsVisible: PropTypes.bool,
    loading: PropTypes.bool,
    currenciesList: PropTypes.array,
    rates: PropTypes.object,
    convertCurrentCurrency: PropTypes.func,
    t: PropTypes.object
};

ProfileAccountSettings.defaultProps = {
    currency: '',
    prevCurrency: '',
    balanceAmount: '',
    modalIsVisible: false,
    loading: false,
    currenciesList: [],
    rates: {},
    convertCurrentCurrency: () => {},
    t: {}
};

export default withTranslation(ProfileAccountSettings);
